import { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, MessageSquare, AlertTriangle, Heart, Award, GraduationCap,
  Users, Clock, CheckCircle2, TrendingUp, BookOpen, Star, HelpCircle,
  School, Sparkles, FileText, BarChart3, Filter, UserCheck, Calendar
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/ui/UserAvatar';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';

interface FilhoResumo {
  id: string;
  nome: string;
  matricula?: string;
  fotoUrl?: string;
  turma?: { nome: string; serie?: string; turno?: string };
  escola?: { nome: string };
}

interface Observacao {
  id: string;
  tipo: 'COMPORTAMENTO' | 'EMOCIONAL' | 'ACADEMICO' | 'SAUDE' | 'DESTAQUE' | 'RELATORIO' | 'OUTRO';
  titulo: string;
  data: string;
  autorNome: string;
  autorCargo?: string;
  descricao: string;
  nivel: 'INFO' | 'ATENCAO' | 'CRITICO';
  alunoId?: string;
  alunoNome?: string;
}

interface DesempenhoAluno {
  aluno: { id: string; nome: string };
  mediaGlobal: number;
  frequencia: {
    presencas: number;
    ausencias: number;
    porcentagem: number;
    diasLetivos: number;
  };
  materias: Array<{ materia: string; media: number; totalNotas: number }>;
}

const TIPO_CONFIG: Record<string, { icon: any; label: string; cor: string; bg: string }> = {
  COMPORTAMENTO: { icon: Users, label: 'Comportamento & Convivência', cor: '#d97706', bg: '#fef3c7' },
  EMOCIONAL: { icon: Heart, label: 'Desenvolvimento Socioemocional', cor: '#db2777', bg: '#fce7f3' },
  ACADEMICO: { icon: GraduationCap, label: 'Desempenho Acadêmico', cor: '#2563eb', bg: '#eff6ff' },
  SAUDE: { icon: AlertTriangle, label: 'Saúde & Rotina Escolar', cor: '#dc2626', bg: '#fef2f2' },
  DESTAQUE: { icon: Award, label: 'Elogio & Destaque', cor: '#059669', bg: '#ecfdf5' },
  RELATORIO: { icon: FileText, label: 'Parecer Pedagógico', cor: '#7c3aed', bg: '#f5f3ff' },
  OUTRO: { icon: BookOpen, label: 'Anotação Geral', cor: '#475569', bg: '#f1f5f9' },
};

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function ResponsavelObservacoes() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const responsavelId = useResponsavelId();

  const [filhos, setFilhos] = useState<FilhoResumo[]>([]);
  const [selectedFilhoId, setSelectedFilhoId] = useState<string>('');
  const [observacoes, setObservacoes] = useState<Observacao[]>([]);
  const [desempenhoList, setDesempenhoList] = useState<DesempenhoAluno[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS');

  useEffect(() => {
    if (!responsavelId) return;
    
    setLoading(true);
    // 1. Carregar lista real de filhos
    api.get(ROUTES.mobileResponsavel.resumo(responsavelId))
      .then((res) => {
        const alunosReais = res.data?.alunos || [];
        const dataFilhos: FilhoResumo[] = alunosReais.map((vinculo: any) => {
          const a = vinculo.aluno;
          return {
            id: a.id,
            nome: a.nome,
            matricula: a.matricula,
            fotoUrl: a.fotoUrl || undefined,
            turma: a.turma || undefined,
            escola: a.escola || { nome: user?.nomeEscola || 'Escola Vinculada' },
          };
        });

        setFilhos(dataFilhos);
        if (dataFilhos.length > 0) {
          setSelectedFilhoId(dataFilhos[0].id);
        }
      })
      .catch((err) => console.error('Erro ao buscar filhos:', err))
      .finally(() => setLoading(false));

    // 2. Carregar dados reais de desempenho para o comparativo
    api.get(ROUTES.mobileResponsavel.desempenho(responsavelId))
      .then((res) => {
        if (Array.isArray(res.data)) {
          setDesempenhoList(res.data);
        }
      })
      .catch((err) => console.warn('Aviso ao carregar desempenho:', err));
  }, [responsavelId, user?.nomeEscola]);

  useEffect(() => {
    if (!responsavelId) return;

    // 3. Carregar observações 100% reais do banco (sem injeção de dados falsos)
    api.get(ROUTES.mobileResponsavel.observacoes(responsavelId))
      .then((res) => {
        const data = res.data || [];
        const mapped: Observacao[] = data.map((obs: any) => {
          const catMap: Record<string, string> = {
            COMPORTAMENTO: 'COMPORTAMENTO',
            APRENDIZAGEM: 'ACADEMICO',
            SAUDE: 'SAUDE',
            SOCIAL: 'COMPORTAMENTO',
            FAMILIAR: 'EMOCIONAL',
            ELOGIO: 'DESTAQUE',
            OUTRO: 'OUTRO',
          };
          const nivelMap: Record<string, string> = {
            INFORMATIVO: 'INFO',
            ATENCAO: 'ATENCAO',
            URGENTE: 'CRITICO',
          };
          return {
            id: obs.id,
            tipo: (catMap[obs.categoria] || 'OUTRO') as Observacao['tipo'],
            titulo: obs.titulo || 'Registro Pedagógico',
            data: obs.dataReferencia || obs.criadoEm,
            autorNome: obs.autor?.nome || 'Educador(a) Regente',
            autorCargo: obs.autor?.cargo || 'Docente',
            descricao: obs.descricao,
            nivel: (nivelMap[obs.nivelAtencao] || 'INFO') as Observacao['nivel'],
            alunoId: obs.alunoId,
            alunoNome: obs.aluno?.nome,
          };
        });

        setObservacoes(mapped);
      })
      .catch((err) => {
        console.error('Erro ao buscar observações:', err);
        setObservacoes([]);
      });
  }, [responsavelId]);

  const selectedFilho = filhos.find(f => f.id === selectedFilhoId);
  const selectedDesempenho = desempenhoList.find(d => d.aluno?.id === selectedFilhoId);

  const observacoesFiltradas = useMemo(() => {
    return observacoes.filter((obs) => {
      const matchFilho = !obs.alunoId || obs.alunoId === selectedFilhoId;
      if (!matchFilho) return false;
      if (filtroTipo === 'TODOS') return true;
      if (filtroTipo === 'DESTAQUE') return obs.tipo === 'DESTAQUE';
      if (filtroTipo === 'ATENCAO') return obs.nivel === 'ATENCAO' || obs.nivel === 'CRITICO';
      if (filtroTipo === 'RELATORIO') return obs.tipo === 'RELATORIO' || obs.tipo === 'ACADEMICO';
      return obs.tipo === filtroTipo;
    });
  }, [observacoes, selectedFilhoId, filtroTipo]);

  const handleOpenChatComProfessor = () => {
    const nomeFilho = selectedFilho?.nome || 'meu filho';
    window.dispatchEvent(
      new CustomEvent('ac:chat:open', {
        detail: {
          prefill: `Olá, sou responsável pelo aluno(a) ${nomeFilho} e gostaria de solicitar um atendimento sobre o desenvolvimento pedagógico.`,
        },
      })
    );
  };

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    const totalDoFilho = observacoes.filter(o => !o.alunoId || o.alunoId === selectedFilhoId);
    const totalElogios = totalDoFilho.filter(o => o.tipo === 'DESTAQUE').length;
    const totalAtencao = totalDoFilho.filter(o => o.nivel === 'ATENCAO' || o.nivel === 'CRITICO').length;
    const totalPedagogico = totalDoFilho.filter(o => o.tipo === 'ACADEMICO' || o.tipo === 'RELATORIO').length;

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Full-Width Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <MessageSquare size={12} color="#818cf8" /> Acompanhamento & Diário de Classe
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Observações & Relatórios do Professor
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Registros pedagógicos, pareceres de conduta e relatórios oficiais lançados pelos docentes
            </p>
          </div>

          {/* Hero KPIs Reais */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{totalDoFilho.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Total</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{totalElogios}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Elogios</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#a78bfa', lineHeight: 1.1 }}>{totalPedagogico}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Pareceres</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: totalAtencao > 0 ? '#fbbf24' : '#94a3b8', lineHeight: 1.1 }}>{totalAtencao}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Atenção</div>
            </div>
          </div>
        </div>

        {/* ═══ Seletor de Dependente e Filtros de Categoria ═══ */}
        <div style={{
          background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '14px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
          marginBottom: 20, boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
        }}>
          {/* Seletor de Filho */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Aluno:</span>
            {filhos.map((filho) => {
              const active = filho.id === selectedFilhoId;
              return (
                <button
                  key={filho.id}
                  onClick={() => setSelectedFilhoId(filho.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 14px', borderRadius: 12, border: '1px solid',
                    borderColor: active ? '#2563eb' : '#e2e8f0',
                    background: active ? '#eff6ff' : '#ffffff',
                    color: active ? '#1e40af' : '#334155',
                    fontWeight: active ? 700 : 500, fontSize: '0.82rem',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <UserAvatar src={filho.fotoUrl} name={filho.nome} role="ALUNO" size="xs" />
                  <span>{filho.nome}</span>
                  {filho.turma?.nome && (
                    <span style={{ fontSize: '0.72rem', color: active ? '#2563eb' : '#94a3b8', background: active ? '#dbeafe' : '#f1f5f9', padding: '1px 6px', borderRadius: 6 }}>
                      {filho.turma.nome}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filtros de Categoria */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { key: 'TODOS', label: 'Todos os Registros' },
              { key: 'DESTAQUE', label: 'Elogios & Destaques' },
              { key: 'RELATORIO', label: 'Pareceres Pedagógicos' },
              { key: 'ATENCAO', label: 'Pontos de Atenção' },
            ].map((cat) => (
              <button
                key={cat.key}
                onClick={() => setFiltroTipo(cat.key)}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: '1px solid',
                  borderColor: filtroTipo === cat.key ? '#2563eb' : '#e2e8f0',
                  background: filtroTipo === cat.key ? '#eff6ff' : '#ffffff',
                  color: filtroTipo === cat.key ? '#2563eb' : '#475569',
                  fontSize: '0.74rem', fontWeight: filtroTipo === cat.key ? 700 : 500,
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* ═══ 2-COLUMN BALANCED DESKTOP SPLIT LAYOUT ═══ */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 0.75fr)',
          gap: 20,
          alignItems: 'start',
          width: '100%',
        }}>
          {/* ─── Coluna Esquerda: Feed Real de Observações e Pareceres ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando anotações pedagógicas...</div>
            ) : observacoesFiltradas.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '48px 24px', background: '#ffffff', borderRadius: 20,
                border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
              }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#2563eb' }}>
                  <MessageSquare size={28} />
                </div>
                <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '1.05rem', fontWeight: 800 }}>
                  Nenhum registro pedagógico localizado
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.84rem', lineHeight: 1.55, maxWidth: 520, margin: '0 auto 18px' }}>
                  Os professores e a coordenação pedagógica lançam anotações de desenvolvimento, elogios, pareceres bimestrais e pontos de atenção diretamente no Diário de Classe ao longo do período letivo.
                </p>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 16px', fontSize: '0.78rem', color: '#475569',
                }}>
                  <CheckCircle2 size={15} color="#16a34a" /> Registros oficiais são sincronizados automaticamente assim que publicados.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {observacoesFiltradas.map((obs) => {
                  const config = TIPO_CONFIG[obs.tipo] || TIPO_CONFIG.OUTRO;
                  const Icon = config.icon;

                  return (
                    <div
                      key={obs.id}
                      style={{
                        background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 14,
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: '0.72rem', fontWeight: 800, color: config.cor, background: config.bg,
                          padding: '4px 10px', borderRadius: 8, letterSpacing: '0.2px',
                        }}>
                          <Icon size={14} /> {config.label}
                        </span>
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={13} />
                          {obs.data ? new Date(obs.data).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Data não informada'}
                        </span>
                      </div>

                      <div>
                        {obs.titulo && (
                          <h4 style={{ margin: '0 0 6px', fontSize: '0.94rem', fontWeight: 800, color: '#0f172a' }}>
                            {obs.titulo}
                          </h4>
                        )}
                        <p style={{ margin: 0, fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.55, fontWeight: 500 }}>
                          "{obs.descricao}"
                        </p>
                      </div>

                      <div style={{ height: 1, background: '#f1f5f9', margin: '2px 0' }} />

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.76rem', color: '#64748b' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <UserCheck size={14} color="#2563eb" />
                          <span style={{ fontWeight: 700, color: '#0f172a' }}>{obs.autorNome}</span>
                          {obs.autorCargo && <span>• {obs.autorCargo}</span>}
                        </div>
                        <span style={{ color: '#16a34a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle2 size={13} /> Registrado no Diário de Classe
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Coluna Direita: Painel de Desempenho Real & Atendimento ─── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Card: Desempenho Acadêmico Real */}
            <div style={{
              background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.94rem' }}>
                <BarChart3 size={18} color="#2563eb" /> Síntese do Aluno no Período
              </div>
              <p style={{ fontSize: '0.76rem', color: '#64748b', margin: 0, lineHeight: 1.45 }}>
                Indicadores consolidados a partir do diário de frequência e lançamento de notas da turma.
              </p>

              {selectedDesempenho ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Média Geral */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>Média Geral do Aluno</span>
                      <span style={{ fontWeight: 800, color: (selectedDesempenho.mediaGlobal ?? 0) >= 6 ? '#16a34a' : '#d97706' }}>
                        {(selectedDesempenho.mediaGlobal ?? 0) > 0 ? `${(selectedDesempenho.mediaGlobal ?? 0).toFixed(1)} / 10.0` : 'Sem notas no período'}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, Math.max(0, ((selectedDesempenho.mediaGlobal ?? 0) / 10) * 100))}%`,
                        height: '100%', background: (selectedDesempenho.mediaGlobal ?? 0) >= 6 ? '#16a34a' : '#2563eb', borderRadius: 999
                      }} />
                    </div>
                  </div>

                  {/* Assiduidade Real */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>Presença Acumulada</span>
                      <span style={{ fontWeight: 800, color: (selectedDesempenho.frequencia?.porcentagem ?? 100) >= 75 ? '#2563eb' : '#dc2626' }}>
                        {selectedDesempenho.frequencia?.porcentagem ?? 100}% ({selectedDesempenho.frequencia?.presencas ?? 0} presenças)
                      </span>
                    </div>
                    <div style={{ width: '100%', height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ width: `${selectedDesempenho.frequencia?.porcentagem ?? 100}%`, height: '100%', background: '#2563eb', borderRadius: 999 }} />
                    </div>
                  </div>

                  {/* Total de Disciplinas */}
                  <div style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: 12, border: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.76rem', color: '#64748b' }}>Componentes Curriculares Avaliados</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>{(selectedDesempenho.materias || []).length} matérias</span>
                  </div>
                </div>
              ) : (
                <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, textAlign: 'center', border: '1px solid #f1f5f9' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                    Aguardando lançamento das avaliações do bimestre pelos professores da turma.
                  </p>
                </div>
              )}
            </div>

            {/* Card: Canal de Comunicação com a Escola */}
            <div style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
              borderRadius: 20, border: '1px solid #bfdbfe', padding: 20,
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e40af', fontWeight: 800, fontSize: '0.88rem' }}>
                <MessageSquare size={16} color="#2563eb" /> Atendimento Pedagógico
              </div>
              <p style={{ fontSize: '0.76rem', color: '#334155', lineHeight: 1.5, margin: 0 }}>
                Tem dúvidas sobre o desenvolvimento acadêmico ou comportamento do seu filho? Solicite orientações diretamente pelo Chat oficial do sistema.
              </p>
              <button
                onClick={handleOpenChatComProfessor}
                style={{
                  background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: 10,
                  padding: '10px 16px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: '0 2px 6px rgba(37,99,235,0.2)', transition: 'all 0.15s',
                }}
              >
                <MessageSquare size={14} /> Conversar pelo Chat do Sistema
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ MOBILE VIEW ═══
  return (
    <div className="resp-mobile-root" style={{ background: '#0b1526' }}>
      {/* ═══ NAVY HEADER ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 0 24px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', marginBottom: 16 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ 
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', 
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', 
              color: 'white', cursor: 'pointer' 
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'white' }}>
              Observações & Pareceres
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
              Diário de classe e conduta escolar
            </p>
          </div>
        </div>

        {/* Seletor de Filho Mobile */}
        {filhos.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '0 20px', overflowX: 'auto', paddingBottom: 4 }}>
            {filhos.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFilhoId(f.id)}
                style={{
                  background: f.id === selectedFilhoId ? '#2563eb' : 'rgba(255,255,255,0.1)',
                  color: 'white', border: 'none', borderRadius: 20, padding: '6px 14px',
                  fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
                }}
              >
                {f.nome}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 90, marginTop: -16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Carregando registros...</div>
        ) : observacoesFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, background: 'white', borderRadius: 16, border: '1px solid #f1f5f9' }}>
            <MessageSquare size={36} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '0.95rem', fontWeight: 700 }}>Nenhuma observação registrada</h3>
            <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 }}>
              Os pareceres e apontamentos dos professores aparecerão aqui assim que lançados no Diário de Classe.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {observacoesFiltradas.map((obs) => {
              const config = TIPO_CONFIG[obs.tipo] || TIPO_CONFIG.OUTRO;
              return (
                <div key={obs.id} style={{ background: 'white', borderRadius: 16, padding: 16, border: '1px solid #f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: config.cor, background: config.bg, padding: '3px 8px', borderRadius: 6 }}>
                      {config.label}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                      {obs.data ? new Date(obs.data).toLocaleDateString('pt-BR') : ''}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.84rem', color: '#1e293b', lineHeight: 1.5 }}>
                    "{obs.descricao}"
                  </p>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f8fafc', fontSize: '0.72rem', color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
                    <span>👨‍🏫 {obs.autorNome}</span>
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>Diário Oficial</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ BARRA INFERIOR DE NAVEGAÇÃO MOBILE ═══ */}
      <ResponsavelTabBar activeTab="obs" />
    </div>
  );
}
